export interface FormatAmountOptions {
  amount: number | string;
  isFiat: boolean;
  decimals: number;
  displayDecimals?: number;
  useGrouping?: boolean;
  showLessThan?: boolean;
  trimZeros?: boolean;
}

export function formatAmount({
  amount,
  isFiat,
  decimals,
  displayDecimals,
  useGrouping = true,
  showLessThan = true,
  trimZeros = true,
}: FormatAmountOptions): string {
  let value = Number(amount);

  if (!Number.isFinite(value)) {
    return "0";
  }

  const negative = value < 0;
  value = Math.abs(value);

  const safeDecimals =
    Number.isFinite(decimals) && decimals >= 0
      ? Math.min(Math.floor(decimals), 100)
      : isFiat
        ? 2
        : 8;

  let precision = displayDecimals ?? (isFiat ? 2 : Math.min(safeDecimals, 8));

  // Never exceed blockchain precision
  precision = Math.min(precision, safeDecimals);

  // Ensure precision is always valid for toLocaleString()
  precision = Math.max(0, Math.min(precision, 100));

  const smallestVisible = Math.pow(10, -precision);

  if (showLessThan && value > 0 && value < smallestVisible) {
    return `${negative ? "-" : ""}<${smallestVisible.toFixed(precision)}`;
  }

  let formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: isFiat ? 2 : 0,
    maximumFractionDigits: precision,
    useGrouping,
  });

  if (trimZeros && formatted.includes(".")) {
    formatted = formatted
      .replace(/(\.\d*?[1-9])0+$/, "$1")
      .replace(/\.0+$/, "");
  }

  return negative ? `-${formatted}` : formatted;
}
